/**
 * FallFilterBar — gemeinsame Filter-Leiste für Fallübersichten (Forderungen #264,
 * später Stellungnahmen #243). Die Komponente ist dumm: sie nimmt den Filter-State
 * plus die verfügbaren Optionen entgegen und ruft `onChange` mit dem neuen State auf.
 * URL-Serialisierung übernimmt der aufrufende `useFallFilter`-Hook.
 */
import { useState } from 'react';
import {
  EMPTY_FALL_FILTER,
  countActiveFallFilters,
  type FallFilterState,
  type TriState,
  type YesNo,
  type VerjaehrungTage,
  type Plausibilitaet,
} from '@/lib/fall-filter-url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Filter, X } from 'lucide-react';

export interface FallFilterOption {
  value: string;
  label: string;
}

export interface FallFilterBarProps {
  state: FallFilterState;
  onChange: (next: FallFilterState) => void;
  statusOptions?: FallFilterOption[];
  businessUnitOptions?: FallFilterOption[];
  versicherungOptions?: FallFilterOption[];
  /** Filter-Buttons, die für den konkreten Einsatzort keinen Sinn ergeben, können ausgeblendet werden. */
  hide?: Partial<Record<
    | 'businessUnit'
    | 'status'
    | 'versicherung'
    | 'abtretung'
    | 'anwalt'
    | 'schadennummer'
    | 'verjaehrung'
    | 'plausibilitaet'
    | 'betrag'
    | 'rechnungsdatum'
    | 'mietbeginn',
    boolean
  >>;
}

const TRI_OPTIONS: { value: TriState; label: string }[] = [
  { value: 'ja', label: 'Ja' },
  { value: 'nein', label: 'Nein' },
  { value: 'offen', label: 'Offen' },
];
const YES_NO_OPTIONS: { value: YesNo; label: string }[] = [
  { value: 'ja', label: 'Ja' },
  { value: 'nein', label: 'Nein' },
];
const VERJAEHRUNG_OPTIONS: { value: VerjaehrungTage; label: string }[] = [
  { value: 30, label: '30 Tage' },
  { value: 60, label: '60 Tage' },
  { value: 90, label: '90 Tage' },
  { value: 180, label: '180 Tage' },
];
const PLAUSIBILITAET_OPTIONS: { value: Plausibilitaet; label: string; color: string }[] = [
  { value: 'gruen', label: 'Grün', color: 'bg-green-500' },
  { value: 'gelb', label: 'Gelb', color: 'bg-yellow-500' },
  { value: 'rot', label: 'Rot', color: 'bg-red-500' },
];

function toggleInArray<T extends string>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  testId,
}: {
  label: string;
  options: FallFilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  testId?: string;
}) {
  if (options.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testId} className="gap-1.5">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2">
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => onChange(toggleInArray(selected, opt.value))}
                data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => onChange([])}>
            Auswahl löschen
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SingleSelect<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  testId,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T | null;
  onChange: (next: T | null) => void;
  testId?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testId} className="gap-1.5">
          {label}
          {selected !== null && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
              {options.find((o) => o.value === selected)?.label ?? String(selected)}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2">
        <div className="space-y-0.5">
          {options.map((opt) => (
            <button
              key={String(opt.value)}
              data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
              onClick={() => onChange(selected === opt.value ? null : opt.value)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted ${selected === opt.value ? 'bg-muted font-medium' : ''}`}
            >
              {opt.label}
            </button>
          ))}
          {selected !== null && (
            <button
              onClick={() => onChange(null)}
              className="w-full text-left px-2 py-1.5 rounded text-xs text-muted-foreground hover:bg-muted"
            >
              Auswahl löschen
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function FallFilterBar({
  state,
  onChange,
  statusOptions = [],
  businessUnitOptions = [],
  versicherungOptions = [],
  hide = {},
}: FallFilterBarProps) {
  const [rangeOpen, setRangeOpen] = useState(false);
  const active = countActiveFallFilters(state);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fall-filter-bar">
      {!hide.businessUnit && (
        <MultiSelect
          label="Business Unit"
          options={businessUnitOptions}
          selected={state.businessUnit}
          onChange={(v) => onChange({ ...state, businessUnit: v })}
          testId="filter-bu"
        />
      )}
      {!hide.status && (
        <MultiSelect
          label="Status"
          options={statusOptions}
          selected={state.status}
          onChange={(v) => onChange({ ...state, status: v })}
          testId="filter-status"
        />
      )}
      {!hide.versicherung && (
        <MultiSelect
          label="Versicherung"
          options={versicherungOptions}
          selected={state.versicherung}
          onChange={(v) => onChange({ ...state, versicherung: v })}
          testId="filter-versicherung"
        />
      )}
      {!hide.abtretung && (
        <SingleSelect
          label="Abtretung"
          options={TRI_OPTIONS}
          selected={state.abtretung}
          onChange={(v) => onChange({ ...state, abtretung: v as TriState | null })}
          testId="filter-abtretung"
        />
      )}
      {!hide.anwalt && (
        <SingleSelect
          label="Anwalt"
          options={YES_NO_OPTIONS}
          selected={state.anwalt}
          onChange={(v) => onChange({ ...state, anwalt: v as YesNo | null })}
          testId="filter-anwalt"
        />
      )}
      {!hide.schadennummer && (
        <SingleSelect
          label="Schadennr."
          options={YES_NO_OPTIONS}
          selected={state.schadennummer}
          onChange={(v) => onChange({ ...state, schadennummer: v as YesNo | null })}
          testId="filter-schadennummer"
        />
      )}
      {!hide.verjaehrung && (
        <SingleSelect
          label="Verjährung in"
          options={VERJAEHRUNG_OPTIONS}
          selected={state.verjaehrungTage}
          onChange={(v) => onChange({ ...state, verjaehrungTage: v as VerjaehrungTage | null })}
          testId="filter-verjaehrung"
        />
      )}
      {!hide.plausibilitaet && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="filter-plausibilitaet" className="gap-1.5">
              Plausibilität
              {state.plausibilitaet.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                  {state.plausibilitaet.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2">
            <div className="space-y-1">
              {PLAUSIBILITAET_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                  <Checkbox
                    checked={state.plausibilitaet.includes(opt.value)}
                    onCheckedChange={() =>
                      onChange({ ...state, plausibilitaet: toggleInArray(state.plausibilitaet, opt.value) })
                    }
                    data-testid={`filter-plausibilitaet-opt-${opt.value}`}
                  />
                  <span className={`w-2.5 h-2.5 rounded-full ${opt.color}`} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {(!hide.betrag || !hide.rechnungsdatum || !hide.mietbeginn) && (
        <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="filter-ranges" className="gap-1.5">
              <Filter className="w-3.5 h-3.5" />
              Bereich
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3">
            <div className="space-y-3">
              {!hide.betrag && (
                <div>
                  <Label className="text-xs text-muted-foreground">Betrag (€)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      placeholder="von"
                      data-testid="filter-betrag-min"
                      value={state.betragMin ?? ''}
                      onChange={(e) =>
                        onChange({ ...state, betragMin: e.target.value === '' ? null : Number(e.target.value) })
                      }
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <Input
                      type="number"
                      placeholder="bis"
                      data-testid="filter-betrag-max"
                      value={state.betragMax ?? ''}
                      onChange={(e) =>
                        onChange({ ...state, betragMax: e.target.value === '' ? null : Number(e.target.value) })
                      }
                    />
                  </div>
                </div>
              )}
              {!hide.rechnungsdatum && (
                <div>
                  <Label className="text-xs text-muted-foreground">Rechnungsdatum</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="date"
                      data-testid="filter-rechnung-von"
                      value={state.rechnungVon ?? ''}
                      onChange={(e) => onChange({ ...state, rechnungVon: e.target.value || null })}
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <Input
                      type="date"
                      data-testid="filter-rechnung-bis"
                      value={state.rechnungBis ?? ''}
                      onChange={(e) => onChange({ ...state, rechnungBis: e.target.value || null })}
                    />
                  </div>
                </div>
              )}
              {!hide.mietbeginn && (
                <div>
                  <Label className="text-xs text-muted-foreground">Mietbeginn</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="date"
                      data-testid="filter-mietbeginn-von"
                      value={state.mietbeginnVon ?? ''}
                      onChange={(e) => onChange({ ...state, mietbeginnVon: e.target.value || null })}
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <Input
                      type="date"
                      data-testid="filter-mietbeginn-bis"
                      value={state.mietbeginnBis ?? ''}
                      onChange={(e) => onChange({ ...state, mietbeginnBis: e.target.value || null })}
                    />
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {active > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(EMPTY_FALL_FILTER)}
          className="gap-1.5 text-muted-foreground"
          data-testid="filter-reset"
        >
          <X className="w-3.5 h-3.5" />
          Reset ({active})
        </Button>
      )}
    </div>
  );
}
